import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import { Modal, Loader } from 'sa-ui';
import { Alert } from 'react-bootstrap';
import Magnifier from 'react-magnifier';
import classNames from 'classnames';
import merge from 'lodash/merge';
import * as pdfUtils from 'app/utils/pdf';
import messages from './simulatedCorrectionMessages';
import './SimulatedCorrectionEditModal.scss';

const SimulatedCorrectionEditModal = (props) => {
  const [answersReviewed, setAnswersReviewed] = useState([]);
  const [fileSrc, setFileSrc] = useState();
  const [optionLanguage, setOptionLanguage] = useState('0');
  // ID of the student whose data is currently loaded in studentAnswer.
  // When props.student.idUser differs, data is stale → show loader immediately during render.
  const [loadedForStudentId, setLoadedForStudentId] = useState(null);

  const isLoadingDetail = props.show && loadedForStudentId !== (props.student ? props.student.idUser : null);

  useEffect(() => {
    if (props.show) {
      props.getStudentAnswerDetail({
        idEvaluation: props.idEvaluation,
        idStudent: props.student.idUser,
        idESCourse: props.idESCourse,
        idCSchool: props.idCSchool,
      });
    } else {
      setLoadedForStudentId(null);
    }
  }, [props.show, props.student && props.student.idUser]);

  useEffect(() => {
    convertPdfToBase64();
    if (props.studentAnswer.languageSelected) {
      setOptionLanguage(props.studentAnswer.languageSelected);
    }
  }, [props.studentAnswer]);

  useEffect(() => {
    if (
      props.studentAnswer.questions.length > 0 ||
      props.studentAnswer.message === 'There is no student on the list'
    ) {
      setLoadedForStudentId(props.student ? props.student.idUser : null);
    }
    setAnswersReviewed({ ...props.studentAnswer.questionsAnswered });
  }, [props.studentAnswer.questions]);

  const handleChangeAnswer = (question, questionNumber) => (event) => {
    const alternativeAnswered = question.alternatives.find(
      (a) => a.alternative === event.target.value.toUpperCase(),
    );
    setAnswersReviewed({
      ...answersReviewed,
      [questionNumber]: alternativeAnswered ? alternativeAnswered.id : undefined,
    });
  };

  function convertPdfToBase64() {
    if (props.studentAnswer.urlViewFile) {
      const fileUrl = props.studentAnswer.urlViewFile;
      fetch(fileUrl)
        .then((response) => response.blob())
        .then((blob) => {
          if (/pdf/gi.test(blob.type)) {
            return pdfUtils.toImageBase64(fileUrl);
          }
          return fileUrl;
        })
        .then((src) => {
          setFileSrc(src);
        });
    }
  }

  function isQuestionHit(question, questionNumber) {
    const idAlternativeAnswered = answersReviewed[questionNumber];
    if (!idAlternativeAnswered) return false;
    return question.alternatives.some(
      (a) => a.id === idAlternativeAnswered && Number(a.correct) === 1,
    );
  }

  function getAlternative(question, questionNumber) {
    const idAlternativeAnswered = answersReviewed[questionNumber];
    return question.alternatives.find((a) => a.id === idAlternativeAnswered);
  }

  function restore() {
    setAnswersReviewed({ ...props.studentAnswer.questionsAnswered });
  }

  function requestRemove() {
    props.onRequestRemove({
      idAttempt: props.studentAnswer.idAttempt,
      idEvaluationAnswer: props.studentAnswer.idEvaluationAnswer,
      idEvaluation: props.idEvaluation,
    });
  }

  function save() {
    const answers = Object.keys(answersReviewed)
      .map((questionNumber) => {
        const { idQuestion } = props.studentAnswer.questions[questionNumber - 1];
        return { [idQuestion]: answersReviewed[questionNumber] || null };
      })
      .reduce(merge, {});
    props.onSave(answers, props.studentAnswer.languageOption === '1' && optionLanguage);
  }

  const totalHits = props.studentAnswer.questions.filter((question, questionIndex) =>
    isQuestionHit(question, questionIndex + 1),
  ).length;
  const totalQuestions = props.studentAnswer.questions.length;
  const hitsPercentage = Math.round((totalHits / totalQuestions) * 100) || 0;

  return (
    <Modal show={props.show} onClose={props.onClose} medium>
      <div className="simulated-correction-edit-modal">
        <div className="card-unity card-unity-audit">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              <p className="m-0">
                <strong>
                  {props.student.registration} - {props.student.studentName}
                </strong>
              </p>
            </div>
          </div>
        </div>
        {isLoadingDetail ? (
          <div className="text-center mt-5 mb-5">
            <Loader simple />
          </div>
        ) : (
          <>
            {props.studentAnswer.warnings && (
              <p className="modal-color rounded border border-warning p-3 text-center">
                <i className="ti-alert mr-2" />
                <em>
                  <FormattedHTMLMessage {...messages.warning} />
                </em>
              </p>
            )}
            {props.studentAnswer.message === 'There is no student on the list' && (
              <Alert style={{ marginTop: '40px' }} variant="danger">
                <FormattedHTMLMessage {...messages.noStudentOnList} />
              </Alert>
            )}
            <div className="row answers mt-4">
              <div className="col-6">
                {props.studentAnswer.languageOption === '1' && (
                  <div className="simulated-laguage">
                    <FormattedHTMLMessage {...messages.languageOption} />
                    <div className="simulated-switch">
                      <div>
                        <input
                          id="englishLanguage"
                          name="englishLanguage"
                          type="checkbox"
                          value="0"
                          checked={optionLanguage === '0'}
                          onChange={() => setOptionLanguage('0')}
                        />
                        <label htmlFor="englishLanguage">
                          <FormattedHTMLMessage {...messages.englishLanguage} />
                        </label>
                      </div>
                      <div>
                        <input
                          id="spanishLanguage"
                          name="spanishLanguage"
                          type="checkbox"
                          value="1"
                          checked={optionLanguage === '1'}
                          onChange={() => setOptionLanguage('1')}
                        />
                        <label htmlFor="spanishLanguage">
                          <FormattedHTMLMessage {...messages.spanishLanguage} />
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <FormattedMessage {...messages.answers}>
                  {(txt) => <h4 className="card-title text-warning">{txt}</h4>}
                </FormattedMessage>
                <div className="row mt-4">
                  {props.studentAnswer.questions.map((question, questionIndex) => {
                    const questionNumber = questionIndex + 1;
                    const hit = isQuestionHit(question, questionNumber);
                    const alternative = getAlternative(question, questionNumber);
                    return (
                      <div className="col-3">
                        <div className="row align-items-center mb-3">
                          <div className="col-3 pr-0">{questionIndex + 1}.</div>
                          <div className="col pr-4">
                            <input
                              className={classNames('form-control', {
                                'is-invalid': !alternative,
                                'is-valid': hit,
                              })}
                              type="text"
                              placeholder="!"
                              onChange={handleChangeAnswer(question, questionNumber)}
                              value={alternative ? alternative.alternative : ''}
                              maxLength="1"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="col-6">
                {props.studentAnswer.idOnline && (
                  <div>
                    <FormattedMessage {...messages.answerCard}>
                      {(txt) => <h4 className="card-title text-warning mb-4">{txt}</h4>}
                    </FormattedMessage>
                    <Magnifier className="rounded img-fluid" src={fileSrc} alt="" />
                  </div>
                )}
                <FormattedMessage {...messages.previewResult}>
                  {(txt) => <div className="card-title text-warning mt-4">{txt}:</div>}
                </FormattedMessage>
                <p>
                  {totalHits} {props.intl.formatMessage(messages.hits)} (
                  {props.intl.formatMessage(messages.questionsLabel, { total: totalQuestions })})
                  <br />
                  {hitsPercentage}% {props.intl.formatMessage(messages.hitLabel)}
                </p>
              </div>
            </div>
            <div className="row mt-5">
              <div className="col-4">
                <FormattedMessage {...messages.restore}>
                  {(txt) => (
                    <button
                      type="button"
                      className="btn btn-block btn-outline-warning btn-lg"
                      onClick={restore}
                    >
                      {txt}
                    </button>
                  )}
                </FormattedMessage>
              </div>
              <div className="col-4">
                <FormattedMessage {...messages.remove}>
                  {(txt) => (
                    <button
                      type="button"
                      className="btn btn-block btn-danger btn-lg"
                      onClick={requestRemove}
                    >
                      {txt}
                    </button>
                  )}
                </FormattedMessage>
              </div>
              <div className="col-4">
                <FormattedMessage {...messages.save}>
                  {(txt) => (
                    <button
                      type="button"
                      className="btn btn-block btn-success btn-lg"
                      onClick={save}
                    >
                      {txt}
                    </button>
                  )}
                </FormattedMessage>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

SimulatedCorrectionEditModal.defaultProps = {
  student: {},
  studentAnswer: { questions: [] },
};

export default injectIntl(SimulatedCorrectionEditModal);
